import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { MapContainer, TileLayer, Polygon, CircleMarker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { ZoomIn, ZoomOut, Maximize2, X } from "lucide-react";
import ProjectPanel from "./ProjectPanel";
import DevicePanel from "./DevicePanel";
import { dashboardAPI, noeudsAPI, automatisationsAPI } from "../../api/services";
import ManualOverrideModal from "../modals/ManualOverrideModal";
import { useSocket } from "../../context/SocketContext";
// Badge HTML pour le nombre de noeuds
const countBadge = (count) => count > 0
  ? `<div style="position:absolute;top:-6px;right:-10px;min-width:18px;height:18px;background:#3b82f6;border:2px solid white;border-radius:9px;display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:10px;padding:0 4px;box-shadow:0 1px 3px rgba(0,0,0,0.3);">${count}</div>`
  : '';

// Créer icône selon état du projet
const createIcon = (pct, etat, nodeCount) => {
  // Projet terminé → pin bleu avec checkmark + badge
  if (etat === 'Terminé') {
    return L.divIcon({
      html: `<div style="position:relative;width:24px;height:32px;">
        <svg width="24" height="32" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg" style="filter:drop-shadow(0 2px 4px rgba(0,0,0,0.3));">
          <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 20 12 20s12-11 12-20c0-6.627-5.373-12-12-12z" fill="#3b82f6" stroke="white" stroke-width="2"/>
          <path d="M8 12.5l3 3 5-5.5" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
        </svg>
        ${countBadge(nodeCount)}
      </div>`,
      className: "",
      iconSize: [34, 32],
      iconAnchor: [12, 32],
    });
  }
  // Projet en cours → pin orange avec icône travaux SVG + badge
  return L.divIcon({
    html: `<div style="position:relative;width:24px;height:32px;">
      <svg width="24" height="32" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg" style="filter:drop-shadow(0 2px 4px rgba(0,0,0,0.3));">
        <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 20 12 20s12-11 12-20c0-6.627-5.373-12-12-12z" fill="#f59e0b" stroke="white" stroke-width="2"/>
        <path d="M12 5L6 15h12L12 5z" fill="white" stroke="white" stroke-width="0.5" stroke-linejoin="round"/>
        <text x="12" y="14" text-anchor="middle" font-size="7" font-weight="bold" fill="#f59e0b">!</text>
      </svg>
      ${countBadge(nodeCount)}
    </div>`,
    className: "",
    iconSize: [34, 32],
    iconAnchor: [12, 32],
  });
};

function ClusterMap({ projets, onSelectProjet }) {
  const map = useMap();
  const clusterRef = useRef(null);
  useEffect(() => {
    if (clusterRef.current) {
      map.removeLayer(clusterRef.current);
    }

    require("leaflet.markercluster");
    const cluster = L.markerClusterGroup({
      maxClusterRadius: 60,
      iconCreateFunction: (c) => L.divIcon({
        html: `<div style="
          width:36px;height:36px;
          background:#3b82f6;
          border:3px solid white;
          border-radius:50%;
          display:flex;align-items:center;justify-content:center;
          color:white;font-weight:bold;font-size:13px;
          box-shadow:0 2px 8px rgba(0,0,0,0.3);
        ">${c.getChildCount()}</div>`,
        className: "",
        iconSize: [36, 36],
        iconAnchor: [18, 18],
      }),
    });

    projets.forEach((p) => {
      if (!p.lat || !p.lng) return;
      const total = p.appareils?.length || p.total || 0;
      const actifs = p.appareils?.filter(a => a.etat === "En ligne").length ?? p.actifs ?? 0;
      const pct = total > 0 ? Math.round((actifs / total) * 100) : 0;
      const marker = L.marker([p.lat, p.lng], { icon: createIcon(pct, p.etat, total) });
      const etatLabel = p.etat === 'Terminé' ? ' ✓ Terminé' : '';
      marker.bindTooltip(`<b>${p.clientNom || ""}</b><br/>${p.nom}${etatLabel}<br/>${total} appareils`, {
        permanent: false, direction: "top",
      });
      marker.on("click", () => onSelectProjet(p));
      cluster.addLayer(marker);
    });

    map.addLayer(cluster);
    clusterRef.current = cluster;

    return () => { if (clusterRef.current) map.removeLayer(clusterRef.current); };
  }, [map, projets]);

  return null;
}

function MapControls() {
  const map = useMap();
  return (
    <div className="absolute right-3 top-3 z-[1000] flex flex-col gap-1">
      <button onClick={() => map.zoomIn()} className="w-8 h-8 bg-slate-800 text-white rounded flex items-center justify-center hover:bg-slate-700">
        <ZoomIn size={14} />
      </button>
      <button onClick={() => map.zoomOut()} className="w-8 h-8 bg-slate-800 text-white rounded flex items-center justify-center hover:bg-slate-700">
        <ZoomOut size={14} />
      </button>
      <button onClick={() => map.setView([38, 8], 4)} className="w-8 h-8 bg-slate-800 text-white rounded flex items-center justify-center hover:bg-slate-700">
        <Maximize2 size={14} />
      </button>
    </div>
  );
}

function FlyToProject({ projetId, projets, onSelectProjet }) {
  const map = useMap();
  useEffect(() => {
    if (!projetId) {
      map.setView([38, 8], 4);
      onSelectProjet(null);
      return;
    }
    const projet = projets.find(p => p._id === projetId);
    if (!projet || !projet.lat || !projet.lng) return;

    if (projet.points?.length >= 3) {
      const bounds = L.latLngBounds(projet.points.map(([lat, lng]) => [lat, lng]));
      map.fitBounds(bounds.pad(0.3));
    } else {
      map.setView([projet.lat, projet.lng], 16);
    }

    onSelectProjet(projet);
  }, [projetId, projets, map, onSelectProjet]);
  return null;
}

export default function MapSection({ focusProjetId, filterRevendeur, filterClient, onlyTermine }) {
  const [allProjets, setAllProjets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProjet, setSelectedProjet] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedVille, setSelectedVille] = useState(null);
  const [selectedPays, setSelectedPays] = useState(null);
  const { lastSensorData, lastStatusChange } = useSocket();

  // Filtres upstream (revendeur/client/état) — utilisés pour les pastilles Pays/Villes
  const projetsBase = useMemo(() => {
    let filtered = allProjets;
    if (onlyTermine) {
      filtered = filtered.filter(p => p.etat === 'Terminé');
    }
    if (filterRevendeur) {
      filtered = filtered.filter(p => p.revendeurId === filterRevendeur);
    }
    if (filterClient) {
      filtered = filtered.filter(p => p.clientId === filterClient);
    }
    return filtered;
  }, [allProjets, filterRevendeur, filterClient, onlyTermine]);

  // Filtre final affiché sur la carte = base + sélection ville/pays
  const projets = useMemo(() => {
    let filtered = projetsBase;
    if (selectedVille) {
      filtered = filtered.filter(p => (p.adresse?.split(",")[0]?.trim() || "Inconnu") === selectedVille);
    }
    if (selectedPays) {
      filtered = filtered.filter(p => {
        const parts = p.adresse?.split(",").map(s => s.trim()) || [];
        return (parts[parts.length - 1] || "Inconnu") === selectedPays;
      });
    }
    return filtered;
  }, [projetsBase, selectedVille, selectedPays]);

  // Modal override manuel
  const [overrideModal, setOverrideModal] = useState(null);

  const executeMapCommand = useCallback(async (nodeId, sn, activate, pauseAutoIds = [], force = false) => {
    try {
      await noeudsAPI.command(nodeId, {
        action: activate ? "Activer" : "Désactiver",
        pauseAutomatisations: pauseAutoIds,
        force,
      });
      const newVal = activate ? 1 : 0;
      setAllProjets(prev => prev.map(p => ({
        ...p,
        appareils: p.appareils.map(d =>
          d._id === nodeId ? { ...d, derniereValeur: newVal, derniereMaj: new Date().toISOString() } : d
        ),
      })));
      setSelectedDevice(prev => prev && prev._id === nodeId
        ? { ...prev, derniereValeur: newVal, derniereMaj: new Date().toISOString() }
        : prev
      );
    } catch (err) {
      console.error("Erreur commande actionneur:", err);
    }
  }, []);

  // Activer / Désactiver un actionneur — vérifie autos ET événements actifs
  const handleToggle = useCallback(async (nodeId, sn, activate) => {
    const action = activate ? 'Activer' : 'Désactiver';
    const projet = allProjets.find(p => p.appareils?.some(d => d._id === nodeId));
    const device = projet?.appareils?.find(d => d._id === nodeId);

    let autos = [];
    let eventConflicts = [];
    if (projet?._id) {
      try {
        const res = await automatisationsAPI.byActionneurs({ actionneurs: nodeId, projet: projet._id });
        autos = res.data?.data || [];
      } catch (_) { /* on continue, juste sans avertissement autos */ }
    }
    try {
      const res = await noeudsAPI.commandCheck(nodeId, action);
      eventConflicts = res.data?.data?.eventConflicts || [];
    } catch (_) { /* idem */ }

    if (autos.length > 0 || eventConflicts.length > 0) {
      setOverrideModal({
        nodeId, sn, type: device?.type || '', activate,
        automations: autos,
        eventConflicts,
      });
      return;
    }
    await executeMapCommand(nodeId, sn, activate);
  }, [allProjets, executeMapCommand]);

  // Mise à jour temps réel — données capteur
  useEffect(() => {
    if (!lastSensorData) return;
    setAllProjets(prev => prev.map(p => ({
      ...p,
      appareils: p.appareils.map(d =>
        d.sn === lastSensorData.sn
          ? { ...d, derniereValeur: lastSensorData.value ?? lastSensorData.valeur ?? d.derniereValeur, unite: lastSensorData.unit ?? d.unite, etat: 'En ligne', derniereMaj: new Date().toISOString() }
          : d
      ),
    })));
    // Mettre à jour aussi le device sélectionné
    setSelectedDevice(prev => prev && prev.sn === lastSensorData.sn
      ? { ...prev, derniereValeur: lastSensorData.value ?? lastSensorData.valeur ?? prev.derniereValeur, unite: lastSensorData.unit ?? prev.unite, etat: 'En ligne', derniereMaj: new Date().toISOString() }
      : prev
    );
  }, [lastSensorData]);

  // Mise à jour temps réel — statut
  useEffect(() => {
    if (!lastStatusChange) return;
    setAllProjets(prev => prev.map(p => {
      const updated = p.appareils.map(d =>
        d.sn === lastStatusChange.sn ? { ...d, etat: lastStatusChange.etat, derniereMaj: new Date().toISOString() } : d
      );
      return {
        ...p,
        appareils: updated,
        actifs: updated.filter(d => d.etat === 'En ligne').length,
      };
    }));
    setSelectedDevice(prev => prev && prev.sn === lastStatusChange.sn
      ? { ...prev, etat: lastStatusChange.etat }
      : prev
    );
  }, [lastStatusChange]);

  useEffect(() => {
    const fetchMapData = async () => {
      try {
        const res = await dashboardAPI.mapData();
        const data = res.data?.data || res.data;
        const projetsList = data.projets || [];
        const noeudsList = data.noeuds || [];

        // Construire les projets avec coordonnées + appareils
        const mapped = projetsList.map(p => {
          const lat = p.points?.[0]?.[0] || null;
          const lng = p.points?.[0]?.[1] || null;
          const appareils = noeudsList.filter(n =>
            (n.projet?._id || n.projet) === p._id
          );
          const total = appareils.length || p.appareils || 0;
          const actifs = appareils.filter(n => n.etat === "En ligne").length || p.appareilsActifs || 0;

          return {
            _id: p._id,
            nom: p.nom,
            etat: p.etat || "",
            lat,
            lng,
            points: p.points || [],
            clientId: p.client?._id || "",
            clientNom: p.client?.nom || "",
            revendeurId: p.revendeur?._id || "",
            revendeurNom: p.revendeur?.nom || "",
            adresse: p.adresse || "",
            total,
            actifs,
            appareils: appareils.map(n => ({
              _id: n._id,
              sn: n.sn,
              type: n.type,
              categorie: n.categorie,
              etat: n.etat,
              uptime: n.uptime,
              derniereMaj: n.derniereMaj,
              derniereValeur: n.derniereValeur,
              unite: n.unite,
              latitude: n.latitude,
              longitude: n.longitude,
            }))
          };
        }).filter(p => p.lat && p.lng);

        setAllProjets(mapped);
      } catch (err) {
        console.error("Erreur chargement carte:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchMapData();
  }, []);

  // Stats globales
  const totalAppareils = projets.reduce((s, p) => s + p.total, 0);
  const totalActifs = projets.reduce((s, p) => s + p.actifs, 0);
  const totalHorsLigne = totalAppareils - totalActifs;

  // Regrouper par pays et par ville (basé sur projetsBase, pour ne pas masquer les autres au clic)
  const pays = useMemo(() => {
    const map = {};
    projetsBase.forEach(p => {
      const adresseParts = p.adresse?.split(",").map(s => s.trim()) || [];
      const paysNom = adresseParts[adresseParts.length - 1] || "Inconnu";
      const villeNom = adresseParts[0] || "Inconnu";

      if (!map[paysNom]) map[paysNom] = { nom: paysNom, villes: {}, appareils: 0 };
      map[paysNom].appareils += p.total;

      if (!map[paysNom].villes[villeNom]) map[paysNom].villes[villeNom] = { nom: villeNom, appareils: 0 };
      map[paysNom].villes[villeNom].appareils += p.total;
    });
    return Object.values(map);
  }, [projetsBase]);

  const villes = useMemo(() => {
    const map = {};
    projetsBase.forEach(p => {
      const adresseParts = p.adresse?.split(",").map(s => s.trim()) || [];
      const villeNom = adresseParts[0] || "Inconnu";
      const paysNom = adresseParts[adresseParts.length - 1] || "";
      if (!map[villeNom]) map[villeNom] = { nom: villeNom, pays: paysNom, appareils: 0 };
      map[villeNom].appareils += p.total;
    });
    return Object.values(map).sort((a, b) => b.appareils - a.appareils).slice(0, 8);
  }, [projetsBase]);

  const uniqueVilles = new Set();
  projets.forEach(p => {
    const v = p.adresse?.split(",")[0]?.trim();
    if (v) uniqueVilles.add(v);
  });

  return (
    <div className="bg-slate-900 rounded-2xl overflow-hidden mb-6">
      {/* Header */}
      <div className="px-3 md:px-5 pt-3 md:pt-4 pb-2 md:pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h3 className="text-white font-semibold">Carte des appareils connectés</h3>
          <p className="text-slate-400 text-xs mt-0.5">
            {loading ? "Chargement..." : `${totalAppareils} appareils IoT répartis dans ${uniqueVilles.size} ville${uniqueVilles.size > 1 ? "s" : ""}`}
          </p>
        </div>
        <div className="flex gap-2">
          <span className="flex items-center gap-1.5 px-3 py-1.5 border border-green-500 rounded-lg text-green-400 text-xs font-medium">
            <div className="w-2 h-2 bg-green-500 rounded-full" /> {totalActifs} en ligne
          </span>
          <span className="flex items-center gap-1.5 px-3 py-1.5 border border-red-500 rounded-lg text-red-400 text-xs font-medium">
            <div className="w-2 h-2 bg-red-500 rounded-full" /> {totalHorsLigne} hors ligne
          </span>
        </div>
      </div>

      {/* Carte */}
      <div className="relative h-64 md:h-96">
        <MapContainer center={[38, 8]} zoom={4} style={{ height: "100%", width: "100%" }} zoomControl={false}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
          <MapControls />
          {!loading && <FlyToProject projetId={focusProjetId} projets={projets} onSelectProjet={setSelectedProjet} />}
          {!loading && <ClusterMap projets={projets} onSelectProjet={setSelectedProjet} />}
          {!loading && projets.filter(p => p.points?.length >= 3).map(p => (
            <Polygon key={p._id} positions={p.points} pathOptions={{ color: "#3b82f6", fillColor: "#3b82f6", fillOpacity: 0.1, weight: 2 }} />
          ))}
          {!loading && projets.flatMap(p =>
            p.appareils.filter(d => d.latitude && d.longitude).map(d => (
              <CircleMarker
                key={d._id}
                center={[d.latitude, d.longitude]}
                radius={6}
                pathOptions={{
                  color: "white",
                  weight: 2,
                  fillColor: d.etat === "En ligne" ? "#22c55e" : "#ef4444",
                  fillOpacity: 1,
                }}
                eventHandlers={{ click: () => { setSelectedProjet(p); setSelectedDevice(d); } }}
              >
                <Tooltip direction="top" offset={[0, -8]}>
                  <b>{d.sn}</b><br />{d.type}<br />{d.etat}
                </Tooltip>
              </CircleMarker>
            ))
          )}
        </MapContainer>

        {/* Légende */}
        <div className="absolute bottom-3 left-3 z-[999] bg-slate-900/90 text-white rounded-xl p-3 text-xs space-y-1.5">
          <p className="font-semibold mb-1">Projets</p>
          <div className="flex items-center gap-2">
            <svg width="14" height="18" viewBox="0 0 24 32" fill="none"><path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 20 12 20s12-11 12-20C24 5.373 18.627 0 12 0z" fill="#f59e0b" stroke="white" strokeWidth="2"/></svg>
            En cours
          </div>
          <div className="flex items-center gap-2">
            <svg width="12" height="16" viewBox="0 0 24 32" fill="none"><path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 20 12 20s12-11 12-20c0-6.627-5.373-12-12-12z" fill="#3b82f6"/><path d="M8 12.5l3 3 5-5.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>
            Terminé
          </div>
          <p className="font-semibold mb-1 mt-2">Appareils</p>
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full border-2 border-white bg-green-500" /> En ligne</div>
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full border-2 border-white bg-red-500" /> Hors ligne</div>
        </div>

        {/* Stats */}
        <div className="absolute bottom-3 right-3 z-[999] flex gap-2">
          <div className="bg-slate-900/90 text-white rounded-xl px-4 py-2 text-center">
            <p className="text-xs text-slate-400">Villes</p>
            <p className="text-xl font-bold">{uniqueVilles.size}</p>
          </div>
          <div className="bg-slate-900/90 text-white rounded-xl px-4 py-2 text-center">
            <p className="text-xs text-slate-400">Appareils</p>
            <p className="text-xl font-bold">{totalAppareils}</p>
          </div>
        </div>
      </div>

      {/* Panel latéral projet */}
      {selectedProjet && (
        <ProjectPanel
          projet={selectedProjet}
          onClose={() => { setSelectedProjet(null); setSelectedDevice(null); }}
          onSelectDevice={setSelectedDevice}
        />
      )}
      {selectedDevice && (
        <DevicePanel
          device={selectedDevice}
          projet={selectedProjet}
          onClose={() => setSelectedDevice(null)}
          onToggle={handleToggle}
        />
      )}

      {/* Bandeau filtre actif */}
      {(selectedVille || selectedPays) && (
        <div className="px-5 pt-3 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-400">Filtré par :</span>
          {selectedPays && (
            <button
              onClick={() => setSelectedPays(null)}
              className="flex items-center gap-1.5 px-2.5 py-1 bg-purple-500/20 border border-purple-500 rounded-full text-purple-300 text-xs font-medium hover:bg-purple-500/30"
            >
              {selectedPays}
              <X size={12} />
            </button>
          )}
          {selectedVille && (
            <button
              onClick={() => setSelectedVille(null)}
              className="flex items-center gap-1.5 px-2.5 py-1 bg-green-500/20 border border-green-500 rounded-full text-green-300 text-xs font-medium hover:bg-green-500/30"
            >
              {selectedVille}
              <X size={12} />
            </button>
          )}
          <span className="text-xs text-slate-500">— {projets.length} projet{projets.length > 1 ? 's' : ''}</span>
        </div>
      )}

      {/* Pays */}
      {pays.length > 0 && (
        <div className="px-5 py-4 border-t border-slate-700 flex gap-4 overflow-x-auto">
          {pays.map(p => {
            const active = selectedPays === p.nom;
            return (
              <button
                key={p.nom}
                onClick={() => setSelectedPays(active ? null : p.nom)}
                title={active ? 'Désélectionner' : `Filtrer par ${p.nom}`}
                className={`text-left rounded-xl px-4 py-3 min-w-max transition-all ${
                  active
                    ? 'bg-purple-500/20 ring-2 ring-purple-500'
                    : 'bg-slate-800 hover:bg-slate-700'
                }`}
              >
                <p className="text-white font-semibold text-sm">{p.nom}</p>
                <p className="text-slate-400 text-xs">{Object.keys(p.villes).length} villes - {p.appareils} app.</p>
                <div className="w-full h-1 bg-purple-500 rounded mt-2" />
              </button>
            );
          })}
        </div>
      )}

      {/* Villes */}
      {villes.length > 0 && (
        <div className="px-5 pb-4 flex gap-3 overflow-x-auto">
          {villes.map((v, i) => {
            const active = selectedVille === v.nom;
            return (
              <button
                key={v.nom}
                onClick={() => setSelectedVille(active ? null : v.nom)}
                title={active ? 'Désélectionner' : `Filtrer par ${v.nom}`}
                className={`text-left rounded-xl px-4 py-3 min-w-max transition-all ${
                  active
                    ? 'bg-green-500/20 ring-2 ring-green-500'
                    : 'bg-slate-800 hover:bg-slate-700'
                }`}
              >
                <div className="flex items-center justify-between gap-6 mb-2">
                  <p className="text-white text-sm font-semibold">{v.nom}</p>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full text-white ${
                    i === 0 ? "bg-orange-500" : i <= 2 ? "bg-blue-500" : "bg-slate-600"
                  }`}>#{i + 1}</span>
                </div>
                <p className="text-slate-400 text-xs">{v.appareils} appareils</p>
                <div className="w-full h-1 bg-green-500 rounded mt-2" />
              </button>
            );
          })}
        </div>
      )}

      {/* Modal override manuel */}
      {overrideModal && (
        <ManualOverrideModal
          isOpen={!!overrideModal}
          onClose={() => setOverrideModal(null)}
          actionneur={{ sn: overrideModal.sn, type: overrideModal.type }}
          action={overrideModal.activate ? "Activer" : "Désactiver"}
          automations={overrideModal.automations}
          eventConflicts={overrideModal.eventConflicts || []}
          onConfirm={({ pauseAutoIds, force }) => {
            executeMapCommand(overrideModal.nodeId, overrideModal.sn, overrideModal.activate, pauseAutoIds, force);
            setOverrideModal(null);
          }}
        />
      )}
    </div>
  );
}
